import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { X, Menu, ExternalLink, CalendarDays, ChevronDown } from "lucide-react";
import { api, asset, applyTheme, BACKEND } from "@/lib/api";
import { resolveSkin, skinRootProps, barClass } from "@/lib/skin";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { LeadDialog } from "@/components/LeadForms";
import { ChatWidget } from "@/components/viewer/ChatWidget";
import { StartScreen } from "@/components/viewer/TourGate";
import { GlideCaption, ClosingCard, AgentBadge } from "@/components/viewer/TourExtras";
import { TdvStage } from "@/threedvista/TdvStage";
import { tourSrc, goTo } from "@/threedvista/bridge";
import { TdvUnlock, tdvTrack } from "@/threedvista/TdvAccess";
import { PinMedia, useTourSound, useFullscreen, BarTools, HelpCard, PointsNav, FloorPlanDialog, MapDialog } from "@/threedvista/TdvExtras";
import { HeroStart, InfoStrip, MiniPlan, SceneNav, ShareDialog, ContactTab } from "@/threedvista/TdvKeystone";
import { InfoWindow } from "@/threedvista/InfoWindow";
import { DemoPanel, demoTour, DEMO_ON } from "@/threedvista/TdvDemoPanel";
import { THEMES } from "@/lib/skin";

export const groupScenes = (tour, list) => {
  const out = [];
  list.forEach((s) => {
    const g = (tour.scene_groups?.[s.id] || "").trim();
    let grp = out.find((x) => x.name === g);
    if (!grp) { grp = { name: g, scenes: [] }; out.push(grp); }
    grp.scenes.push(s);
  });
  return out;
};

function SceneButton({ s, current, onGo }) {
  return (
    <button onClick={() => onGo(s.index)} className={`w-full flex items-center gap-3 p-2 rounded-2xl text-left text-sm transition-colors ${current === s.index ? "bg-white/15" : "hover:bg-white/10"}`} data-testid={`tdv-scene-${s.index}`}>
      {s.thumb ? <img src={s.thumb} alt="" className="w-14 h-10 rounded-lg object-cover shrink-0" /> : <span className="w-14 h-10 rounded-lg bg-white/10 shrink-0" />}
      <span className="line-clamp-2">{s.label}</span>
    </button>
  );
}

function ScenePanel({ groups, title, current, onGo, open, onClose, top = "top-20" }) {
  return (
    <aside className={`absolute z-20 left-3 ${top} bottom-3 w-64 rounded-3xl glass-dark text-white p-3 overflow-y-auto transition-[opacity,transform] duration-300 ${open ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6 pointer-events-none"}`} data-testid="tdv-scene-panel">
      <div className="flex items-center justify-between px-2 pb-2">
        <p className="text-[11px] uppercase tracking-[0.25em] text-white/60" data-testid="tdv-menu-title">{title}</p>
        <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/10 grid place-items-center" data-testid="tdv-scene-panel-close"><X className="w-4 h-4" /></button>
      </div>
      {groups.map((g) => (
        <div key={g.name || "_"} className="mb-2">
          {g.name && <p className="px-2 pt-3 pb-1 text-xs font-semibold text-white/80" data-testid={`tdv-group-${g.name}`}>{g.name}</p>}
          {g.scenes.map((s) => <SceneButton key={s.id} s={s} current={current} onGo={onGo} />)}
        </div>
      ))}
    </aside>
  );
}

const MENU_CLS = "rounded-2xl border-white/10 bg-[#0b1626]/95 backdrop-blur-xl text-white p-2";
const ITEM_CLS = "rounded-xl focus:bg-white/10 focus:text-white cursor-pointer";
const TRIG_CLS = "h-9 px-3 rounded-full text-sm hover:bg-white/10 inline-flex items-center gap-1 whitespace-nowrap";

function OwnMenus({ items, onItem }) {
  return items.map((it) => (it.kind === "menu" ? (
    <DropdownMenu key={it.id}>
      <DropdownMenuTrigger className={TRIG_CLS} data-testid={`tdv-own-menu-${it.id}`}>{it.label}<ChevronDown className="w-3.5 h-3.5" /></DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={MENU_CLS}>
        {it.children.map((c) => <DropdownMenuItem key={c.id} onSelect={() => onItem(c)} className={ITEM_CLS} data-testid={`tdv-own-menu-item-${c.id}`}>{c.label}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : <button key={it.id} onClick={() => onItem(it)} className={TRIG_CLS} data-testid={`tdv-own-menu-${it.id}`}>{it.label}</button>));
}

function MobileMenu({ items, onItem }) {
  const flat = items.flatMap((it) => (it.kind === "menu" ? it.children.map((c) => ({ ...c, group: it.label })) : [it]));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={TRIG_CLS} data-testid="tdv-mobile-menu">Menu<ChevronDown className="w-3.5 h-3.5" /></DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={`${MENU_CLS} max-h-[70vh] overflow-y-auto`}>
        {flat.map((c) => <DropdownMenuItem key={c.id} onSelect={() => onItem(c)} className={ITEM_CLS} data-testid={`tdv-mobile-menu-item-${c.id}`}>{c.group && <span className="text-white/50 mr-1">{c.group} ·</span>}{c.label}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TopMenus({ groups, onGo, items = [], onItem }) {
  const named = groups.filter((g) => g.name);
  const own = items.filter((it) => it.label?.trim()).map((it) => (it.kind === "menu" ? { ...it, children: (it.children || []).filter((c) => c.label?.trim()) } : it)).filter((it) => it.kind !== "menu" || it.children.length);
  if (!named.length && !own.length) return null;
  return (
    <>
    <nav className="hidden lg:flex items-center gap-1 ml-4 min-w-0" data-testid="tdv-top-menus">
      {named.map((g) => (
        <DropdownMenu key={g.name}>
          <DropdownMenuTrigger className={TRIG_CLS} data-testid={`tdv-top-menu-${g.name}`}>{g.name}<ChevronDown className="w-3.5 h-3.5" /></DropdownMenuTrigger>
          <DropdownMenuContent align="start" className={MENU_CLS}>
            {g.scenes.map((s) => <DropdownMenuItem key={s.id} onSelect={() => onGo(s.index)} className={ITEM_CLS}>{s.label}</DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
      ))}
      <OwnMenus items={own} onItem={onItem} />
    </nav>
    {own.length > 0 && <div className="lg:hidden"><MobileMenu items={own} onItem={onItem} /></div>}
    </>
  );
}

export function PinCard({ pin, onClose }) {
  if (!pin) return null;
  return (
    <div className="absolute z-30 left-3 bottom-3 w-[min(22rem,calc(100%-1.5rem))] max-h-[calc(100%-6rem)] overflow-y-auto rounded-3xl glass-dark text-white p-5 rise" data-testid="tdv-pin-card">
      <button onClick={onClose} className="absolute right-3 top-3 w-8 h-8 rounded-full hover:bg-white/10 grid place-items-center" data-testid="tdv-pin-card-close"><X className="w-4 h-4" /></button>
      {pin.image_url && <img src={asset(pin.image_url)} alt="" className="w-full h-36 object-cover rounded-2xl mb-3" />}
      <p className="font-serif text-xl pr-8" data-testid="tdv-pin-card-title">{pin.title || "Details"}</p>
      {pin.body && <p className="text-sm text-white/80 mt-2 whitespace-pre-line">{pin.body}</p>}
      {pin.link_url && <a href={pin.link_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold underline" data-testid="tdv-pin-card-link">Learn more <ExternalLink className="w-3.5 h-3.5" /></a>}
      <PinMedia pin={pin} />
    </div>
  );
}

export const named = (tour, scenes) => scenes.filter((s) => !(tour.hidden_scenes || []).includes(s.id)).map((s) => ({ ...s, label: tour.scene_names?.[s.id] || s.label }));

function useCinematic(tdv, tour, scenes, run, onEnd) {
  const [cap, setCap] = useState(null);
  const [closing, setClosing] = useState(false);
  const [active, setActive] = useState(false);
  const stop = useRef(null);
  useEffect(() => {
    if (run && tour.source === "giraffe" && (!tdv || !scenes.length || !tour.intro_glide)) { onEnd(); return undefined; }
    if (!run || !tdv || !scenes.length) return undefined;
    const steps = (tour.glide_steps || []).filter((s) => scenes.some((x) => x.id === s.media_id));
    if (!tour.intro_glide || !steps.length) { onEnd(); return undefined; }
    let alive = true;
    const timers = [];
    const wait = (ms) => new Promise((r) => { timers.push(setTimeout(r, ms)); });
    const end = () => { if (!alive) return; alive = false; timers.forEach(clearTimeout); setCap(null); setClosing(false); setActive(false); const first = scenes[0]; if (first) goTo(tdv, first.index); onEnd(); };
    stop.current = end;
    setActive(true);
    (async () => {
      for (const s of steps) {
        if (!alive) return;
        const idx = scenes.find((x) => x.id === s.media_id).index;
        setCap(null);
        if (tdv.glide) {
          const g = tdv.glide(idx, (s.hold || 5) * 1000 + 700);
          await wait(1300);
          if (!alive) return;
          setCap({ text: s.text || tour.scene_names?.[s.media_id] || "" });
          await g;
          continue;
        }
        goTo(tdv, idx);
        await wait(700);
        if (!alive) return;
        setCap({ text: s.text || tour.scene_names?.[s.media_id] || "" });
        await wait((s.hold || 5) * 1000);
      }
      if (!alive) return;
      setCap(null);
      if (tour.closing_card !== false) { setClosing(true); await wait(5000); }
      end();
    })();
    return () => { alive = false; timers.forEach(clearTimeout); };
  }, [run, tdv, scenes]); // eslint-disable-line react-hooks/exhaustive-deps
  return { cap, closing, active, skip: () => stop.current?.() };
}

export default function TdvViewer() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [tdv, setTdv] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [current, setCurrent] = useState(-1);
  const [panel, setPanel] = useState(window.innerWidth >= 768);
  const [pin, setPin] = useState(null);
  const [intro, setIntro] = useState(true);
  const [ui, setUi] = useState(false);
  const [lead, setLead] = useState(false);
  const preview = new URLSearchParams(window.location.search).get("skin_preview") === "1";
  const [pv, setPv] = useState(null);
  useEffect(() => {
    if (!preview) return undefined;
    const on = (e) => { if (e.origin === window.location.origin && e.data?.type === "sh-skin") setPv(e.data.skin); };
    window.addEventListener("message", on);
    window.parent?.postMessage({ type: "sh-skin-ready" }, window.location.origin);
    return () => window.removeEventListener("message", on);
  }, [preview]);
  useEffect(() => { if (pv) applyTheme(pv); }, [pv]);
  const [plan, setPlan] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [help, setHelp] = useState(false);
  const [share, setShare] = useState(false);
  const pending = useRef(null);
  const t0 = data?.tour || {};
  const curScene = scenes.find((s) => s.index === current);
  const sound = useTourSound(t0, curScene?.id, ui && !preview);
  const fs = useFullscreen();
  useEffect(() => {
    if (!ui || preview || t0.help_card === false || localStorage.getItem(`tdv_help_${id}`)) return;
    localStorage.setItem(`tdv_help_${id}`, "1");
    setHelp(true);
  }, [ui]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => api.get(`/public/tdv/${id}`).then((r) => { setData(r.data); applyTheme(r.data.builder); document.title = r.data.tour.name; setIntro(!preview && r.data.tour.start_screen !== false); }).catch(() => setErr("This tour isn't available."));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!ui || !data?.tour?.id || preview) return undefined;
    tdvTrack(id, "view");
    const iv = setInterval(() => { if (!document.hidden) tdvTrack(id, "ping"); }, 30000);
    return () => clearInterval(iv);
  }, [ui, data?.tour?.id, id]);
  const [demoOn, setDemoOn] = useState(DEMO_ON);
  const [demoTheme, setDemoTheme] = useState("");
  const tour = useMemo(() => (data?.tour?.demo ? demoTour(data.tour, demoOn) : data?.tour), [data, demoOn]);
  const list = useMemo(() => (tour ? named(tour, scenes) : []), [tour, scenes]);
  const cine = useCinematic(tdv, preview ? {} : tour || {}, list, !!tour && !intro && !ui, () => setUi(true));
  if (err) return <div className="min-h-screen grid place-items-center bg-slate-950 text-white" data-testid="tdv-viewer-error">{err}</div>;
  if (!tour) return <div className="min-h-screen bg-slate-950" />;
  if (data.locked) return <TdvUnlock data={data} onDone={load} />;
  const b = data.builder;
  const skin = pv || resolveSkin(b, null);
  const hide = ui ? "opacity-100" : "opacity-0 pointer-events-none";
  const home = { name: tour.name, description: tour.description, start_intro: tour.description, photos: tour.photo_url ? [tour.photo_url] : [] };
  const visible = new Set(list.map((s) => s.id));
  const points = tour.numbered && tour.own_hotspots ? tour.hotspots.filter((h) => visible.has(h.media_id)) : [];
  const numbers = Object.fromEntries(points.map((h, i) => [h.id, i + 1]));
  const labels = Object.fromEntries(list.map((s) => [s.id, s.label]));
  const jumpScene = (mid) => { const s = scenes.find((x) => x.id === mid); if (s && tdv) goTo(tdv, s.index); };
  const goPoint = (p) => { if (p.media_id === curScene?.id) setPin(p); else { pending.current = p; jumpScene(p.media_id); } };
  const strip = !!tour.info_strip;
  const top = strip ? "top-[7.5rem]" : "top-20";
  const go = (i) => tdv && goTo(tdv, i);
  const onItem = (it) => {
    tdvTrack(id, "menu", it.id, it.label);
    if (it.kind === "link" && it.url) window.open(it.url, "_blank", "noopener");
    else if (it.kind === "scene" && it.media_id) jumpScene(it.media_id);
    else if (it.kind === "page") setPin({ id: `page-${it.id}`, title: it.label, body: it.body, image_url: it.image_url, video_url: it.video_url, pdf_url: it.pdf_url, pdf_name: it.pdf_name, win: it.win });
  };

  return (
    <div className="fixed inset-0 bg-slate-950" {...skinRootProps(skin)} data-testid="tdv-viewer">
      <TdvStage src={tourSrc(BACKEND, tour)} giraffe={tour.source === "giraffe" ? tour : null} onInfo={(d) => { if (ui) tdvTrack(id, "hotspot", d.hotspotId || "", (d.content?.text || "Info point").slice(0, 80)); }} native={tour.native_hotspots} hiddenNative={tour.hidden_native || []} hotspots={tour.own_hotspots && ui ? tour.hotspots : []}
        onReady={(t, s) => { setTdv(t); setScenes(s); }} onScene={(i, sc) => { setCurrent(i); setPin(pending.current); pending.current = null; if (ui && sc) tdvTrack(id, "scene", sc.id, sc.label); }} onPin={(h) => { tdvTrack(id, "hotspot", h.id, h.title); if (h.goto_media_id) jumpScene(h.goto_media_id); else setPin(h); }} activeId={pin?.id} numbers={numbers} />
      {cine.active && <div className="absolute inset-0 z-[15]" onClick={cine.skip} data-testid="tdv-cine-skip" />}
      <GlideCaption cap={cine.cap} />
      <ClosingCard show={cine.closing} house={home} builder={b} unit="ft" onBook={() => { cine.skip(); setLead(true); }} bookLabel="Book a viewing" agent={data.agent} />
      {strip && <div className={`transition-opacity duration-700 ${hide}`}><InfoStrip tour={tour} /></div>}
      <header className={`absolute z-20 ${strip ? "top-12" : "top-3"} left-3 right-3 h-14 rounded-2xl ${["dark", "glass"].includes(skin.bar_style) ? "glass-dark text-white" : `${barClass(skin.bar_style)} ${skin.bar_style === "light" ? "" : "text-white"}`} flex items-center gap-3 px-3 transition-opacity duration-700 ${hide}`} data-testid="tdv-topbar">
        <button onClick={() => setPanel(!panel)} className="w-10 h-10 rounded-full hover:bg-white/10 grid place-items-center" data-testid="tdv-scenes-toggle"><Menu className="w-5 h-5" /></button>
        {b.logo_url ? <img src={asset(b.logo_url)} alt={b.name} className="h-8 w-auto max-w-[9rem] object-contain" /> : <span className="font-semibold truncate max-w-[8rem] sm:max-w-none">{b.name}</span>}
        <span className="text-white/30 hidden sm:inline">|</span>
        <p className="font-serif text-lg truncate hidden sm:block" data-testid="tdv-tour-name">{tour.name}</p>
        <TopMenus groups={groupScenes(tour, list)} onGo={(i) => tdv && goTo(tdv, i)} items={tour.menu_items || []} onItem={onItem} />
        {current >= 0 && <p className="ml-auto hidden md:block text-sm text-white/70 truncate" data-testid="tdv-current-scene">{list.find((s) => s.index === current)?.label}</p>}
        <div className="ml-auto md:ml-2"><BarTools tour={tour} sound={sound} fs={fs} onPlan={() => setPlan(true)} onMap={() => setMapOpen(true)} onHelp={() => setHelp(true)} onShare={() => setShare(true)} /></div>
        <button onClick={() => setLead(true)} className="ml-1 h-10 px-3 sm:px-4 rounded-full bg-white text-slate-900 text-sm font-semibold inline-flex items-center gap-2 shrink-0" data-testid="tdv-book-button"><CalendarDays className="w-4 h-4" /><span className="hidden sm:inline">Book a viewing</span></button>
      </header>
      <div className={`transition-opacity duration-700 ${hide}`}>
        <ScenePanel top={top} groups={groupScenes(tour, list)} title={tour.menu_title || "Scenes"} current={current} open={panel && list.length > 0} onClose={() => setPanel(false)} onGo={(i) => { if (tdv) goTo(tdv, i); if (window.innerWidth < 768) setPanel(false); }} />
        {tour.scene_nav && !pin && <SceneNav list={list} current={current} onGo={go} shift={panel && list.length > 0} />}
        {tour.mini_plan && <MiniPlan tour={tour} labels={labels} current={curScene?.id} onPick={jumpScene} onExpand={() => setPlan(true)} top={top} />}
        {tour.contact_tab && <ContactTab label={tour.contact_label} onClick={() => setLead(true)} />}
      </div>
      <InfoWindow pin={pin} onClose={() => setPin(null)} onBook={() => { setPin(null); setLead(true); }} />
      {ui && <div className={`absolute z-20 ${top} left-1/2 -translate-x-1/2`}><PointsNav points={points} current={pin?.id} onGo={goPoint} /></div>}
      <HelpCard open={help && ui} onClose={() => setHelp(false)} tour={tour} sound={sound} hasPlan={!!tour.floorplans?.length} />
      <FloorPlanDialog open={plan} onClose={() => setPlan(false)} tour={tour} labels={labels} current={curScene?.id} onPick={jumpScene} />
      <ShareDialog open={share} onClose={() => setShare(false)} title={tour.name} />
      {tour.demo && ui && <DemoPanel id={id} base={tour.source === "giraffe" ? "/giraffe" : "/3dvista"} on={demoOn} setOn={setDemoOn} theme={demoTheme} onReplay={() => setIntro(true)} setTheme={(k) => { const { name, ...th } = THEMES[k]; const s = { ...resolveSkin(b, null), ...th }; setDemoTheme(k); setPv(s); applyTheme(s); }} />}
      <MapDialog open={mapOpen} onClose={() => setMapOpen(false)} address={tour.address} name={tour.name} />
      <div className={`absolute z-30 right-3 bottom-3 transition-opacity duration-700 ${hide}`}>
        <ChatWidget houseTypeId={tour.id} builderName={b.name} onBook={() => setLead(true)} skin={skin} suggestions={b.chat_suggestions || []} welcome={tour.chat_welcome} chatUrl={`${BACKEND}/api/public/tdv/${tour.id}/chat`} />
      </div>
      {data.agent && <AgentBadge agent={data.agent} className={`absolute z-30 right-3 bottom-20 transition-opacity duration-700 ${hide}`} />}
      {intro && (tour.start_style === "hero" ? <HeroStart tour={tour} b={b} list={list} base={tourSrc(BACKEND, tour)} tdv={tdv} onStart={() => setIntro(false)} />
        : <StartScreen builder={b} house={home} agent={data.agent} onStart={() => setIntro(false)} />)}
      <LeadDialog open={lead} onOpenChange={setLead} kind="viewing" builderId={b.id} context={{ message_prefix: tour.name }} onSent={() => tdvTrack(id, "lead")} />
    </div>
  );
}
